import type { MasterSchema } from '../constants/masters';

export interface MasterCsvRow {
  code: string;
  name: string;
  description?: string;
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  sortOrder?: number;
  isActive?: boolean;
  metadata?: Record<string, unknown>;
}

function escapeCsv(value: unknown): string {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function buildMasterCsvColumns(schema?: MasterSchema | null): { key: string; label: string }[] {
  const cols: { key: string; label: string }[] = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
  ];
  if (schema?.geoFields?.includes('country')) cols.push({ key: 'country', label: 'Country' });
  if (schema?.geoFields?.includes('state')) cols.push({ key: 'state', label: 'State' });
  if (schema?.geoFields?.includes('city')) cols.push({ key: 'city', label: 'City' });
  if (schema?.geoFields?.includes('district')) cols.push({ key: 'district', label: 'District' });
  cols.push({ key: 'sortOrder', label: 'Sort Order' });
  cols.push({ key: 'isActive', label: 'Active' });
  for (const f of schema?.metadataFields || []) {
    cols.push({ key: `metadata.${f.key}`, label: f.label });
  }
  return cols;
}

function getCellValue(row: Record<string, unknown>, key: string): unknown {
  if (key.startsWith('metadata.')) {
    const metaKey = key.slice('metadata.'.length);
    const meta = row.metadata as Record<string, unknown> | undefined;
    return meta?.[metaKey];
  }
  return row[key];
}

export function masterRowsToCsv(
  rows: Record<string, unknown>[],
  schema?: MasterSchema | null,
  includeSerial = true,
): string {
  const cols = buildMasterCsvColumns(schema);
  const headerCols = includeSerial ? [{ key: '__sno', label: 'S.No' }, ...cols] : cols;
  const header = headerCols.map((c) => escapeCsv(c.label)).join(',');
  const body = rows.map((row, index) =>
    headerCols.map((col) => {
      if (col.key === '__sno') return String(index + 1);
      return escapeCsv(getCellValue(row, col.key));
    }).join(','),
  );
  return [header, ...body].join('\n');
}

export function downloadMasterCsv(
  rows: Record<string, unknown>[],
  fileName: string,
  schema?: MasterSchema | null,
) {
  const csv = masterRowsToCsv(rows, schema);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadMasterTemplate(fileName: string, schema?: MasterSchema | null) {
  const cols = buildMasterCsvColumns(schema);
  const header = cols.map((c) => escapeCsv(c.label)).join(',');
  const example = cols.map((col) => {
    if (col.key === 'code') return 'example_code';
    if (col.key === 'name') return 'Example Name';
    if (col.key === 'sortOrder') return '0';
    if (col.key === 'isActive') return 'true';
    return '';
  }).join(',');
  const csv = [header, example].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}-import-template.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

export function parseMasterCsv(text: string, schema?: MasterSchema | null): MasterCsvRow[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, ''));
  const colMap = buildMasterCsvColumns(schema);
  const headerToKey = new Map<string, string>();
  for (const col of colMap) {
    headerToKey.set(col.label.toLowerCase().replace(/\s+/g, ''), col.key);
    headerToKey.set(col.key.toLowerCase(), col.key);
  }
  headerToKey.set('s.no', '__skip');
  headerToKey.set('sno', '__skip');

  const rows: MasterCsvRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    const record: Record<string, unknown> = { metadata: {} };
    headers.forEach((header, idx) => {
      const key = headerToKey.get(header);
      if (!key || key === '__skip') return;
      const value = cells[idx] ?? '';
      if (key.startsWith('metadata.')) {
        const metaKey = key.slice('metadata.'.length);
        (record.metadata as Record<string, unknown>)[metaKey] = value;
      } else if (key === 'sortOrder') {
        record.sortOrder = value === '' ? 0 : Number(value);
      } else if (key === 'isActive') {
        record.isActive = value === '' ? true : ['true', '1', 'yes', 'active'].includes(value.toLowerCase());
      } else {
        record[key] = value;
      }
    });
    if (!record.code && !record.name) continue;
    rows.push({
      code: String(record.code || ''),
      name: String(record.name || ''),
      description: record.description ? String(record.description) : undefined,
      country: record.country ? String(record.country) : undefined,
      state: record.state ? String(record.state) : undefined,
      city: record.city ? String(record.city) : undefined,
      district: record.district ? String(record.district) : undefined,
      sortOrder: typeof record.sortOrder === 'number' ? record.sortOrder : 0,
      isActive: record.isActive !== false,
      metadata: record.metadata as Record<string, unknown>,
    });
  }
  return rows;
}

export function masterCsvRowToPayload(row: MasterCsvRow): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    code: row.code.trim(),
    name: row.name.trim(),
    description: row.description?.trim() || undefined,
    country: row.country?.trim() || undefined,
    state: row.state?.trim() || undefined,
    city: row.city?.trim() || undefined,
    district: row.district?.trim() || undefined,
    sortOrder: row.sortOrder ?? 0,
    isActive: row.isActive !== false,
    metadata: row.metadata || {},
  };
  return payload;
}
